import { useState, useEffect, useCallback } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../ui/card";
import { Tabs, TabsContent } from "../../ui/tabs";
import {
  Plus,
  Trash2,
  FileText,
  BookOpen,
  ChevronDown,
  ChevronUp,
  FileVideo,
  Presentation,
  Clock,
  AlertCircle,
  UploadCloud,
  Loader2,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { toast } from "sonner";
import { Badge } from "../../ui/badge";
import { Alert, AlertDescription } from "../../ui/alert";
import QuizEditor from "./QuizEditor";
import QuestionEditor from "./QuestionEditor";
import FileStatusIndicator from "./FileStatusIndicator";
import { createTopic, updateTopic } from "@/api/topicsApi";
import { uploadLearningMaterial } from "@/api/uploadApi";
import {
  GradingType,
  testApi,
  TestDifficulty,
  TestQuestionType,
  TestType,
} from "@/api/testApi";
import { updateMaterial } from "@/api/materialsApi";
import { handleFileUpload } from "@/api/filesApi";

function areObjectsEqual(obj1, obj2) {
  if (obj1 === obj2) return true;

  if (obj1 === null || obj2 === null) return obj1 === obj2;

  if (typeof obj1 !== "object" || typeof obj2 !== "object") {
    return obj1 === obj2;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (let key of keys1) {
    if (!keys2.includes(key)) return false;

    if (!areObjectsEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

const TopicsStep = ({
  stepLoading,
  mode = "ADD",
  onDataChange,
  initialData = null,
  setCurrentStep,
}) => {
  // Initialize state with initial data if provided (for edit mode)
  const [topics, setTopics] = useState([]);
  const [isSavingQuiz, setIsSavingQuiz] = useState(false);
  const [activeTopicIndex, setActiveTopicIndex] = useState(0);
  const [questionBeingEdited, setQuestionBeingEdited] = useState(null);
  const [editingQuizMaterial, setEditingQuizMaterial] = useState(null);
  const [isQuestionEditorOpen, setIsQuestionEditorOpen] = useState(false);
  const [isEditingQuestion, setIsEditingQuestion] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (initialData) {
      setTopics(initialData);
    } else {
      // Try to load from localStorage if no initialData
      const savedTopicsData = localStorage.getItem("topics");
      if (savedTopicsData) {
        try {
          const parsed = JSON.parse(savedTopicsData);
          if (Array.isArray(parsed)) {
            setTopics(parsed);
          }
        } catch (error) {
          console.warn("Failed to parse saved topics data:", error);
        }
      }
    }
  }, [initialData]);

  // Notify parent of data changes when topics change
  useEffect(() => {
    if (onDataChange) {
      onDataChange({ topics });
    }
  }, [topics, onDataChange]);

  // Material type options
  const materialTypes = [
    { value: "PDF", label: "PDF Document", icon: FileText },
    { value: "PPTX", label: "PPT Document", icon: Presentation },
    { value: "VIDEO", label: "Video", icon: FileVideo },
  ];

  // Add a new topic
  const addTopic = () => {
    const newTopic = {
      id: `topic-${Date.now()}`,
      name: `Topic ${topics.length + 1}`,
      description: "",
      displayOrder: topics.length + 1,
      estimatedDuration: 0,
      learningMaterials: [],
    };

    setTopics([...topics, newTopic]);
    setActiveTopicIndex(topics.length);
  };

  // Update a topic
  const updateTopicData = (index, updates) => {
    const updatedTopics = [...topics];
    updatedTopics[index] = { ...updatedTopics[index], ...updates };
    setTopics(updatedTopics);
  };

  // Update a learning material in a topic
  const updateLearningMaterial = (topicIndex, materialIndex, updates) => {
    const updatedTopics = [...topics];
    updatedTopics[topicIndex] = {
      ...updatedTopics[topicIndex],
      learningMaterials: updatedTopics[topicIndex].learningMaterials.map(
        (material, idx) =>
          idx === materialIndex ? { ...material, ...updates } : material
      ),
    };
    setTopics(updatedTopics);
  };

  // Add a new learning material to a topic
  const addLearningMaterial = (topicIndex) => {
    const currentMaterials = topics[topicIndex].learningMaterials || [];
    const newMaterial = {
      id: `material-${Date.now()}`,
      title: `New Material ${currentMaterials.length + 1}`,
      displayOrder: currentMaterials.length + 1, // Added displayOrder
      duration: 0,
      allowDownload: true, // Default true as per schema
      drmEnabled: true, // Default true as per schema
      content: {
        fileType: "PPTX",
        url: "",
        file: null,
      },
      test: [], // Changed from 'quiz' to 'test' array to match backend
    };
    const updatedTopics = [...topics];
    updatedTopics[topicIndex] = {
      ...updatedTopics[topicIndex],
      learningMaterials: [...currentMaterials, newMaterial],
    };

    setTopics(updatedTopics);
  };

  // Open quiz editor
  const openQuizEditor = (topicIndex, materialIndex) => {
    const updatedTopics = [...topics];
    const currentTests =
      updatedTopics[topicIndex].learningMaterials[materialIndex].test || [];
    // If no test exists, create a new one
    if (currentTests.length === 0) {
      const newTest = {
        id: `test-${Date.now()}`,
        title: `Quiz for ${updatedTopics[topicIndex].learningMaterials[materialIndex].title || "Untitled Material"}`,
        timelimit: 0, // Default 0 minutes
        type: "QUIZ", // Default type
        questionSets: [
          {
            id: `set-${Date.now()}`,
            title: "Quiz Set 1", // Changed from "Quiz Set 1"
            questions: [],
          },
        ],
        questions: [], // Separate questions array as per schema
      };

      updatedTopics[topicIndex].learningMaterials[materialIndex].test = [
        newTest,
      ];
      setTopics(updatedTopics);
    }

    setEditingQuizMaterial({ topicIndex, materialIndex });
  };

  // Close quiz editor
  const closeQuizEditor = () => {
    setEditingQuizMaterial(null);
  };

  // Handle editing a question
  const handleEditQuestion = (question) => {
    setQuestionBeingEdited(question);
    setIsEditingQuestion(true);
    setIsQuestionEditorOpen(true);
  };

  const handleUpdateQuiz = (updatedQuizData) => {
    const updatedTopics = [...topics];
    const topicIndex = editingQuizMaterial.topicIndex;
    const materialIndex = editingQuizMaterial.materialIndex;

    if (updatedTopics[topicIndex]?.learningMaterials?.[materialIndex]) {
      // Ensure test array exists
      if (!updatedTopics[topicIndex].learningMaterials[materialIndex].test) {
        updatedTopics[topicIndex].learningMaterials[materialIndex].test = [];
      }
      if (
        updatedTopics[topicIndex].learningMaterials[materialIndex].test
          .length === 0
      ) {
        updatedTopics[topicIndex].learningMaterials[materialIndex].test = [
          updatedQuizData,
        ];
      } else {
        updatedTopics[topicIndex].learningMaterials[materialIndex].test[0] =
          updatedQuizData;
      }

      setTopics(updatedTopics);
    }
  };

  const handleAddQuestion = (questionData = null, quizSetIndex = 0) => {
    if (questionData) {
      const questionToEdit = questionData || {
        question: "",
        options: ["", "", "", ""],
        correctAnswer: 0,
        explanation: "",
        weight: 1,
        quizSetIndex: 0,
        quizSetTitle: "Quiz Set 1",
      };

      if (typeof questionToEdit.quizSetIndex !== "number") {
        questionToEdit.quizSetIndex = quizSetIndex || 0;
      }

      if (!questionToEdit.quizSetTitle) {
        questionToEdit.quizSetTitle = `Quiz Set ${(quizSetIndex || 0) + 1}`;
      }

      if (!questionToEdit.quizSetId) {
        questionToEdit.quizSetId = `set-${Date.now()}-${quizSetIndex || 0}`;
      }

      setQuestionBeingEdited(questionToEdit);
      setIsEditingQuestion(true);
    } else {
      const newQuestionData = {
        quizSetIndex: quizSetIndex,
        quizSetTitle: `Quiz Set ${quizSetIndex + 1}`,
      };
      setQuestionBeingEdited(newQuestionData);
      setIsEditingQuestion(false);
    }
    setIsQuestionEditorOpen(true);
  };

  const handleFileChange = async (
    e,
    activeTopicIndex,
    materialId,
    fileType
  ) => {
    const file = e.target.files[0];
    if (file) {
      console.log("File upload started:", file.name, file.size, file.type);

      try {
        // Find material by ID
        const currentMaterial = topics[
          activeTopicIndex
        ]?.learningMaterials.find((m) => m.id === materialId);

        if (!currentMaterial) {
          console.error("No material found with ID:", materialId);
          toast.error("Material not found. Please try again.");
          return;
        }

        // Create file data object
        const fileData = {
          file: file,
          filename: file.name,
          fileSize: file.size,
          fileType: fileType,
          fileCategory: "LEARNING_MATERIAL",
          url: URL.createObjectURL(file),
        };

        // Find material index by ID and update
        const materialIndex = topics[
          activeTopicIndex
        ].learningMaterials.findIndex((m) => m.id === materialId);
        if (materialIndex !== -1) {
          updateLearningMaterial(activeTopicIndex, materialIndex, {
            content: fileData,
          });
        }

        toast.success("File uploaded successfully");
      } catch (error) {
        console.error("Error uploading file:", error);
        toast.error("Failed to upload file. Please try again.");
      }
    }
  };

  // Helper function to reset file input
  const resetFileInput = (topicIndex, materialId) => {
    const inputId = `material-file-${topicIndex}-${materialId}`;
    const inputElement = document.getElementById(inputId);
    if (inputElement) {
      inputElement.value = "";
    }
  };

  // Handle re-upload
  const handleReupload = (topicIndex, materialId) => {
    const inputId = `material-file-${topicIndex}-${materialId}`;
    const inputElement = document.getElementById(inputId);
    if (inputElement) {
      inputElement.click();
    }
  };

  // Helper function to format duration
  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) return "0 min";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return mins > 0 ? `${hours}hr ${mins}min` : `${hours}hr`;
    }
    return `${mins}min`;
  };

  // Function to trigger validation and show errors
  const validateAndShowErrors = useCallback(async () => {
    setShowValidationErrors(true);

    const allErrors = [];

    if (!topics || topics.length === 0) {
      allErrors.push("At least one topic is required");
    } else {
      topics.forEach((topic, topicIndex) => {
        if (!topic.name?.trim()) {
          // Changed from 'title' to 'name'
          allErrors.push(`Topic ${topicIndex + 1}: Title is required`);
        }

        if (!topic.learningMaterials || topic.learningMaterials.length === 0) {
          allErrors.push(
            `Topic ${topicIndex + 1}: At least one learning material is required`
          );
        } else {
          topic.learningMaterials.forEach((material, materialIndex) => {
            if (!material.title?.trim()) {
              allErrors.push(
                `Topic ${topicIndex + 1}, Material ${materialIndex + 1}: Title is required`
              );
            }

            if (!material.duration || material.duration <= 0) {
              allErrors.push(
                `Topic ${topicIndex + 1}, Material ${materialIndex + 1}: Duration must be greater than 0`
              );
            }

            if (!material.content?.fileType) {
              allErrors.push(
                `Topic ${topicIndex + 1}, Material ${materialIndex + 1}: File type is required`
              );
            }
          });
        }
      });
    }

    setValidationErrors(allErrors);
    return allErrors.length === 0;
  }, [topics]);

  // Method to validate and get current data
  const validateAndGetData = () => {
    return new Promise((resolve, reject) => {
      validateAndShowErrors().then((isValid) => {
        if (isValid) {
          resolve({ topics });
        } else {
          reject(validationErrors);
        }
      });
    });
  };

  // Expose validation method to parent
  useEffect(() => {
    if (onDataChange) {
      onDataChange({ topics, validate: validateAndGetData });
    }
  }, [topics]);

  // Cleanup file URLs on unmount
  useEffect(() => {
    return () => {
      topics.forEach((topic) => {
        if (topic.learningMaterials) {
          topic.learningMaterials.forEach((material) => {
            if (
              material.content?.url &&
              material.content.url.startsWith("blob:")
            ) {
              URL.revokeObjectURL(material.content.url);
            }
          });
        }
      });
    };
  }, [topics]);

  const renderThumbnailPreview = (material) => {
    const thumbnailUrl = `${import.meta.env.VITE_CDN_LINK}${material.content?.thumbnailUrl}`;
    const filename = material.content?.filename;

    if (thumbnailUrl && material.content?.url && filename) {
      return (
        <a
          href={material.content.url}
          target="_blank"
          className="text-xs text-muted-foreground group transition-all"
        >
          <div className="mt-2 p-2 w-full border rounded-md bg-gray-50">
            <div className="flex items-center space-x-3">
              <div className="relative">
                {material.content?.thumbnailUrl && (
                  <img
                    src={thumbnailUrl}
                    alt="File thumbnail"
                    className="w-16 h-12 object-cover rounded border"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {filename}
                </p>
                <p className="text-xs text-gray-500">
                  Preview Available : View File
                </p>
              </div>
            </div>
          </div>
        </a>
      );
    }
    return null;
  };

  const disableAll = loading || mode === "VIEW";

  return (
    <div className="space-y-6 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Topics & Learning Materials</h2>
      </div>

      {/* Show validation summary only after submit/next attempt */}
      {showValidationErrors &&
        validationErrors.length > 0 &&
        mode !== "VIEW" && (
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-700">
              <div className="font-medium mb-2">
                Please fix the following issues:
              </div>
              <ul className="list-disc list-inside space-y-1">
                {validationErrors.map((error, index) => (
                  <li key={index} className="text-sm">
                    {error}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

      <div className="flex gap-6 flex-1 overflow-hidden">
        {/* Topics sidebar */}
        <div className="w-1/3 flex flex-col gap-2">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={addTopic}
            disabled={disableAll}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Topic
          </Button>

          <div className="space-y-2">
            {topics.map((topic, index) => (
              <div
                key={topic.id}
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                  activeTopicIndex === index
                    ? "border-[var(--primary-font)] bg-[var(--primary-font)]/10"
                    : "hover:bg-muted/50"
                }`}
                onClick={() => setActiveTopicIndex(index)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-start justify-start">
                    <h3 className="font-medium">
                      {topic.name || `Topic ${index + 1}`}{" "}
                      {/* Changed from 'title' to 'name' */}
                    </h3>
                  </div>
                  <div className="flex flex-col items-end justify-end gap-1">
                    <div className="flex gap-2 items-center">
                      {/* Move Up Button */}
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        tabIndex={-1}
                        aria-label="Move topic up"
                        disabled={disableAll || index === 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          const updatedTopics = [...topics];
                          [updatedTopics[index - 1], updatedTopics[index]] = [
                            updatedTopics[index],
                            updatedTopics[index - 1],
                          ];
                          setTopics(updatedTopics);
                          if (activeTopicIndex === index)
                            setActiveTopicIndex(index - 1);
                          else if (activeTopicIndex === index - 1)
                            setActiveTopicIndex(index);
                        }}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      {/* Move Down Button */}
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        tabIndex={-1}
                        aria-label="Move topic down"
                        disabled={disableAll || index === topics.length - 1}
                        onClick={(e) => {
                          e.stopPropagation();
                          const updatedTopics = [...topics];
                          [updatedTopics[index], updatedTopics[index + 1]] = [
                            updatedTopics[index + 1],
                            updatedTopics[index],
                          ];
                          setTopics(updatedTopics);
                          if (activeTopicIndex === index)
                            setActiveTopicIndex(index + 1);
                          else if (activeTopicIndex === index + 1)
                            setActiveTopicIndex(index);
                        }}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="red"
                        size="icon"
                        className="h-6 w-6 cursor-pointer"
                        disabled={disableAll}
                        onClick={(e) => {
                          e.stopPropagation();
                          const updatedTopics = topics.filter(
                            (_, i) => i !== index
                          );
                          setTopics(updatedTopics);
                          if (
                            activeTopicIndex >= updatedTopics.length &&
                            updatedTopics.length > 0
                          ) {
                            setActiveTopicIndex(updatedTopics.length - 1);
                          } else if (updatedTopics.length === 0) {
                            setActiveTopicIndex(-1);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4 shrink-0 text-red-800" />
                      </Button>
                    </div>
                    {topic.learningMaterials?.length > 0 && (
                      <Badge variant="blue" className="rounded !px-4 !py-[2px]">
                        <p className="text-xs m-0 p-0">
                          {topic.learningMaterials.length} material
                          {topic.learningMaterials.length !== 1 ? "s" : ""}
                        </p>
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Topic details and materials */}
        <div className="flex-1 flex flex-col">
          {topics.length > 0 && activeTopicIndex >= 0 ? (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Topic Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor={`topic-title-${activeTopicIndex}`}>
                      Title *
                    </Label>
                    <Input
                      id={`topic-title-${activeTopicIndex}`}
                      value={topics[activeTopicIndex].name || ""} // Changed from 'title' to 'name'
                      onChange={
                        (e) =>
                          updateTopicData(activeTopicIndex, {
                            name: e.target.value,
                          }) // Changed from 'title' to 'name'
                      }
                      placeholder="Enter topic title"
                      disabled={disableAll}
                      className={
                        !topics[activeTopicIndex]?.name?.trim()
                          ? "border-red-300"
                          : ""
                      } // Changed from 'title' to 'name'
                    />
                    {!topics[activeTopicIndex]?.name?.trim() && ( // Changed from 'title' to 'name'
                      <p className="text-sm text-red-600">
                        Topic title is required
                      </p>
                    )}
                  </div>
                  <div className="space-y-2 w-full">
                    <Label htmlFor={`topic-desc-${activeTopicIndex}`}>
                      Description
                    </Label>
                    <div className="w-full">
                      <Textarea
                        id={`topic-desc-${activeTopicIndex}`}
                        value={topics[activeTopicIndex].description}
                        onChange={(e) =>
                          updateTopicData(activeTopicIndex, {
                            description: e.target.value,
                          })
                        }
                        placeholder="Enter topic description"
                        rows={3}
                        className="w-full"
                        disabled={disableAll}
                      />
                    </div>
                  </div>
                  <div className="space-x-2 w-full flex items-center justify-between">
                    <div className="space-y-2 w-full">
                      <Label
                        htmlFor={`topic-estimated-duration-${activeTopicIndex}`}
                      >
                        Estimated Duration (in mins)
                      </Label>
                      <div className="w-full">
                        <Input
                          id={`topic-estimated-duration-${activeTopicIndex}`}
                          type="number"
                          value={
                            topics[activeTopicIndex].estimatedDuration || ""
                          }
                          onChange={(e) =>
                            updateTopicData(activeTopicIndex, {
                              estimatedDuration: parseInt(e.target.value) || 0,
                            })
                          }
                          placeholder="Enter estimated duration"
                          className="w-full"
                          disabled={disableAll}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between py-0">
                  <div>
                    <CardTitle>Learning Materials</CardTitle>
                    <CardDescription>
                      Add learning materials and their associated quizzes. At
                      least one material is required.
                    </CardDescription>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={disableAll}
                    onClick={() => addLearningMaterial(activeTopicIndex)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Material
                  </Button>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  {topics[activeTopicIndex].learningMaterials?.length > 0 ? (
                    <Tabs
                      defaultValue="learningMaterials"
                      className="flex-1 flex flex-col"
                    >
                      <TabsContent
                        value="learningMaterials"
                        className="flex-1 mt-0 pt-0"
                      >
                        <div className="space-y-4 overflow-y-auto max-h-[calc(100vh-400px)] pr-2">
                          {topics[activeTopicIndex].learningMaterials.map(
                            (material, matIndex) => {
                              const hasValidationErrors =
                                !material.title?.trim() ||
                                (!material.content?.file &&
                                  !material.content?.url);

                              return (
                                <Card
                                  key={material.id}
                                  className={`overflow-hidden ${hasValidationErrors ? "border-red-300" : ""}`}
                                >
                                  <div className="p-4">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <FileText className="h-4 w-4 text-muted-foreground" />
                                        <span className="text-sm font-medium">
                                          {material.title}
                                        </span>
                                        {material.duration > 0 && (
                                          <Badge
                                            variant="outline"
                                            className="ml-2"
                                          >
                                            <Clock className="h-3 w-3 mr-1" />
                                            {formatDuration(material.duration)}
                                          </Badge>
                                        )}
                                        {material.test &&
                                          material.test.length > 0 &&
                                          (() => {
                                            // Changed from 'quiz' to 'test'
                                            const firstTest = material.test[0];
                                            const totalQuestions =
                                              firstTest.questionSets?.reduce(
                                                // Changed from 'quizSets' to 'questionSets'
                                                (total, set) =>
                                                  total +
                                                  (set.questions?.length || 0),
                                                0
                                              ) || 0;
                                            const setCount =
                                              firstTest.questionSets?.length ||
                                              0; // Changed from 'quizSets' to 'questionSets'
                                            if (totalQuestions === 0) {
                                              return null;
                                            }

                                            return (
                                              <Badge
                                                variant="outline"
                                                className="ml-2"
                                              >
                                                {`${totalQuestions} Questions in ${setCount} ${setCount === 1 ? "Set" : "Sets"}`}
                                              </Badge>
                                            );
                                          })()}
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8"
                                          onClick={() => {
                                            const newMaterials = [
                                              ...topics[activeTopicIndex]
                                                .learningMaterials,
                                            ];
                                            if (matIndex > 0) {
                                              [
                                                newMaterials[matIndex],
                                                newMaterials[matIndex - 1],
                                              ] = [
                                                newMaterials[matIndex - 1],
                                                newMaterials[matIndex],
                                              ];
                                              updateTopicData(
                                                activeTopicIndex,
                                                {
                                                  learningMaterials:
                                                    newMaterials,
                                                }
                                              );
                                            }
                                          }}
                                          disabled={
                                            matIndex === 0 || disableAll
                                          }
                                        >
                                          <ChevronUp className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8"
                                          onClick={() => {
                                            const newMaterials = [
                                              ...topics[activeTopicIndex]
                                                .learningMaterials,
                                            ];
                                            if (
                                              matIndex <
                                              newMaterials.length - 1
                                            ) {
                                              [
                                                newMaterials[matIndex],
                                                newMaterials[matIndex + 1],
                                              ] = [
                                                newMaterials[matIndex + 1],
                                                newMaterials[matIndex],
                                              ];
                                              updateTopicData(
                                                activeTopicIndex,
                                                {
                                                  learningMaterials:
                                                    newMaterials,
                                                }
                                              );
                                            }
                                          }}
                                          disabled={
                                            matIndex ===
                                              topics[activeTopicIndex]
                                                .learningMaterials.length -
                                                1 || disableAll
                                          }
                                        >
                                          <ChevronDown className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          variant="red"
                                          className="h-8 w-8 text-red-800 cursor-pointer"
                                          disabled={disableAll}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const newMaterials = [
                                              ...topics[activeTopicIndex]
                                                .learningMaterials,
                                            ];
                                            newMaterials.splice(matIndex, 1);
                                            updateTopicData(activeTopicIndex, {
                                              learningMaterials: newMaterials,
                                            });
                                          }}
                                        >
                                          <Trash2 className="!h-4 !w-4 shrink-0" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="p-4 pt-0 space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div className="space-y-2">
                                        <Label
                                          htmlFor={`material-title-${activeTopicIndex}-${matIndex}`}
                                        >
                                          Material Title *
                                        </Label>
                                        <Input
                                          id={`material-title-${activeTopicIndex}-${matIndex}`}
                                          value={material.title}
                                          onChange={(e) =>
                                            updateLearningMaterial(
                                              activeTopicIndex,
                                              matIndex,
                                              { title: e.target.value }
                                            )
                                          }
                                          placeholder="Enter material title"
                                          disabled={disableAll}
                                          className={
                                            !material.title?.trim()
                                              ? "border-red-300"
                                              : ""
                                          }
                                        />
                                        {!material.title?.trim() && (
                                          <p className="text-sm text-red-600">
                                            Material title is required
                                          </p>
                                        )}
                                      </div>

                                      <div className="space-y-2">
                                        <Label
                                          htmlFor={`material-duration-${activeTopicIndex}-${matIndex}`}
                                        >
                                          Duration (minutes)
                                        </Label>
                                        <Input
                                          id={`material-duration-${activeTopicIndex}-${matIndex}`}
                                          type="number"
                                          min="0"
                                          value={material.duration || 0}
                                          onChange={(e) => {
                                            const duration =
                                              parseInt(e.target.value) || 0;
                                            updateLearningMaterial(
                                              activeTopicIndex,
                                              matIndex,
                                              {
                                                duration: Math.max(0, duration),
                                              }
                                            );
                                          }}
                                          placeholder="0"
                                          disabled={disableAll}
                                        />
                                        <p className="text-xs text-muted-foreground">
                                          Estimated time to complete this
                                          material
                                        </p>
                                      </div>
                                    </div>

                                    <div className="space-y-4">
                                      <div className="space-y-2">
                                        <Label>Type</Label>
                                        <Select
                                          value={
                                            material.content.fileType || "PPTX"
                                          }
                                          disabled={disableAll}
                                          onValueChange={(value) => {
                                            updateLearningMaterial(
                                              activeTopicIndex,
                                              matIndex,
                                              {
                                                content: {
                                                  ...material.content,
                                                  fileType: value,
                                                  file: null,
                                                  url: "",
                                                },
                                              }
                                            );
                                            resetFileInput(
                                              activeTopicIndex,
                                              material.id
                                            );
                                          }}
                                        >
                                          <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Select material type" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {materialTypes.map((type) => {
                                              const Icon = type.icon;
                                              return (
                                                <SelectItem
                                                  key={type.value}
                                                  value={type.value}
                                                >
                                                  <div className="flex items-center">
                                                    <Icon className="w-4 h-4 mr-2" />
                                                    {type.label}
                                                  </div>
                                                </SelectItem>
                                              );
                                            })}
                                          </SelectContent>
                                        </Select>
                                      </div>

                                      <div className="space-y-1">
                                        <Label
                                          htmlFor={`material-file-${activeTopicIndex}-${material.id}`}
                                        >
                                          {material.content.fileType === "VIDEO"
                                            ? "Video File *"
                                            : "Document File *"}
                                        </Label>
                                        <div
                                          className={`flex items-center gap-2 ${disableAll && "pointer-events-none cursor-default"}`}
                                        >
                                          <label
                                            htmlFor={`material-file-${activeTopicIndex}-${material.id}`}
                                            className={`flex flex-1 cursor-pointer items-center justify-between rounded-md border px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                                              !material.content?.file &&
                                              !material.content?.url
                                                ? "border-red-300 bg-red-50"
                                                : "border-input bg-white"
                                            }`}
                                          >
                                            <span
                                              className={`truncate ${disableAll && "opacity-50"}`}
                                            >
                                              {material.content?.file?.name ||
                                                material.content?.filename ||
                                                "Choose a file..."}
                                            </span>
                                            <div className="text-muted-foreground flex items-center gap-2">
                                              {mode === "EDIT" &&
                                                material.content && (
                                                  <FileStatusIndicator
                                                    fileData={material.content}
                                                    onReupload={() =>
                                                      handleReupload(
                                                        activeTopicIndex,
                                                        material.id
                                                      )
                                                    }
                                                  />
                                                )}
                                              <UploadCloud
                                                className={`w-4 h-4 ${disableAll && "pointer-events-none cursor-default opacity-50"}`}
                                              />
                                            </div>
                                            <input
                                              id={`material-file-${activeTopicIndex}-${material.id}`}
                                              type="file"
                                              className={`hidden ${disableAll && "pointer-events-none cursor-default opacity-50"}`}
                                              disabled={disableAll}
                                              accept={
                                                material.content.fileType ===
                                                "VIDEO"
                                                  ? "video/*"
                                                  : material.content
                                                        .fileType === "PDF"
                                                    ? ".pdf,.doc,.docx"
                                                    : material.content
                                                          .fileType === "PPTX"
                                                      ? ".ppt,.pptx"
                                                      : ""
                                              }
                                              onChange={(e) => {
                                                e.preventDefault();
                                                handleFileChange(
                                                  e,
                                                  activeTopicIndex,
                                                  material.id,
                                                  material.content.fileType
                                                );
                                              }}
                                            />
                                          </label>
                                          {material.content?.file && (
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              disabled={disableAll}
                                              className="h-8 w-8 cursor-pointer text-red-800"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                updateLearningMaterial(
                                                  activeTopicIndex,
                                                  matIndex,
                                                  {
                                                    content: {
                                                      ...material.content,
                                                      file: null,
                                                      url: "",
                                                    },
                                                  }
                                                );
                                                resetFileInput(
                                                  activeTopicIndex,
                                                  material.id
                                                );
                                              }}
                                            >
                                              <Trash2 className="!h-4 !w-4 shrink-0" />
                                            </Button>
                                          )}
                                        </div>

                                        {!material.content?.file &&
                                          !material.content?.url && (
                                            <p className="text-sm text-red-600">
                                              File is required
                                            </p>
                                          )}

                                        {renderThumbnailPreview(material)}
                                      </div>

                                      <div className="pt-2">
                                        <Button
                                          type="button"
                                          variant="outline"
                                          onClick={() =>
                                            openQuizEditor(
                                              activeTopicIndex,
                                              matIndex
                                            )
                                          }
                                          className="w-full"
                                        >
                                          <FileText className="w-4 h-4" />
                                          {mode === "VIEW"
                                            ? "View Quiz"
                                            : material.test &&
                                                material.test.length > 0 &&
                                                material.test[0]
                                                  ?.questionSets?.[0]?.questions
                                                  ?.length > 0 // Updated condition
                                              ? "Edit Quiz"
                                              : "Add Quiz (Optional)"}
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </Card>
                              );
                            }
                          )}
                        </div>
                      </TabsContent>
                    </Tabs>
                  ) : (
                    <div className="flex-1 flex items-center justify-center py-8 border-2 border-dashed border-red-300 rounded-lg bg-red-50">
                      <div className="text-center">
                        <BookOpen className="mx-auto h-12 w-12 text-red-400" />
                        <h3 className="mt-2 text-sm font-medium text-red-900">
                          No learning materials
                        </h3>
                        <p className="mt-1 text-sm text-red-600">
                          At least one learning material is required. Get
                          started by adding a new material.
                        </p>
                        <div className="mt-4">
                          <Button
                            type="button"
                            onClick={() =>
                              addLearningMaterial(activeTopicIndex)
                            }
                            disabled={disableAll}
                            variant="outline"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add First Material
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-12 border-2 border-dashed border-red-300 rounded-lg bg-red-50">
              <FileText className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="mt-2 text-sm font-medium text-red-900">
                No topics added
              </h3>
              <p className="mt-1 text-sm text-red-600">
                At least one topic is required. Get started by adding your first
                topic.
              </p>
              <div className="mt-6">
                <Button type="button" onClick={addTopic} disabled={disableAll}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Topic
                </Button>
              </div>
            </div>
          )}
        </div>

        {editingQuizMaterial && (
          <>
            {/* Quiz Editor Side Sheet */}
            <QuizEditor
              disabled={disableAll}
              editingQuizMaterial={editingQuizMaterial}
              quizData={
                topics[editingQuizMaterial.topicIndex]?.learningMaterials[
                  editingQuizMaterial.materialIndex
                ]?.test?.[0] || {} // Updated to use test array
              }
              onUpdateQuiz={handleUpdateQuiz}
              addQuestion={handleAddQuestion}
              editQuestion={handleEditQuestion}
              isSaving={isSavingQuiz}
              setIsSaving={setIsSavingQuiz}
              onClose={closeQuizEditor}
              topics={topics}
            />

            {/* Question Editor Sheet */}
            <QuestionEditor
              key={`${editingQuizMaterial?.topicId}-${editingQuizMaterial?.materialId}`}
              topics={topics}
              editingQuizMaterial={editingQuizMaterial}
              questionBeingEdited={questionBeingEdited}
              setQuestionBeingEdited={setQuestionBeingEdited}
              isQuestionEditorOpen={isQuestionEditorOpen}
              setIsQuestionEditorOpen={setIsQuestionEditorOpen}
              isEditing={isEditingQuestion}
              setIsEditing={setIsEditingQuestion}
              onUpdateTopics={setTopics}
            />
          </>
        )}
      </div>

      {mode !== "VIEW" && (
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={() => setCurrentStep((prev) => prev - 1)}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={loading}
            onClick={async () => {
              setLoading(true);
              const courseId = sessionStorage.getItem("courseId");
              const topicData =
                JSON.parse(localStorage.getItem("topics")) || [];
              try {
                for (const topic of topics) {
                  try {
                    if (topic.id.startsWith("topic-")) {
                      // Creating new topic
                      const { learningMaterials, ...topicsData } = topic;
                      const topicCreation = await createTopic({
                        ...topicsData,
                        courseId,
                      });

                      if (!topicCreation?.success || !topicCreation?.data?.id) {
                        console.error(`Failed to create topic: ${topic.title}`);
                        continue;
                      }

                      const topicId = topicCreation.data.id;
                      topic.id = topicId;

                      // Save progress immediately - create serializable version for localStorage
                      const topicsForStorage = topics.map((t) => {
                        const { learningMaterials, ...topicWithoutMaterials } =
                          t;
                        return {
                          ...topicWithoutMaterials,
                          learningMaterials:
                            learningMaterials?.map((m) => {
                              const { content, ...materialWithoutContent } = m;
                              return {
                                ...materialWithoutContent,
                                content: content
                                  ? {
                                      filename: content.filename,
                                      fileSize: content.fileSize,
                                      fileType: content.fileType,
                                      fileCategory: content.fileCategory,
                                    }
                                  : null,
                              };
                            }) || [],
                        };
                      });
                      localStorage.setItem(
                        "topics",
                        JSON.stringify(topicsForStorage)
                      );

                      for (const material of learningMaterials || []) {
                        try {
                          const { test, ...materialData } = material;

                          const learningMaterialData = {
                            topicId,
                            title: materialData.title,
                            description: materialData.description,
                            displayOrder: material.displayOrder,
                            tags: materialData.tags || [],
                            allowDownload: materialData.allowDownload ?? true,
                            drmEnabled: materialData.drmEnabled ?? true,
                          };

                          let contentType;
                          switch (materialData.content?.fileType) {
                            case "PPTX":
                              contentType =
                                "application/vnd.openxmlformats-officedocument.presentationml.presentation";
                              break;
                            case "PDF":
                              contentType = "application/pdf";
                              break;
                            case "VIDEO":
                              contentType = "video/mp4";
                              break;
                            default:
                              contentType = "application/octet-stream";
                          }

                          const materialDataFile = {
                            filename: material.content?.filename,
                            fileSize: material.content?.fileSize,
                            fileType:
                              material.content?.fileType === "PPTX"
                                ? "DOCUMENT"
                                : material.content?.fileType,
                            contentType,
                            category: material.content?.fileCategory,
                            duration: material.duration,
                          };

                          const finalPayload = {
                            ...materialDataFile,
                            learningMaterial: learningMaterialData,
                          };

                          const uploadingLearningMaterial =
                            await uploadLearningMaterial(
                              material.content?.file,
                              finalPayload
                            );

                          if (
                            !uploadingLearningMaterial?.success ||
                            !uploadingLearningMaterial?.data?.materialId
                          ) {
                            console.error(
                              `Failed to upload material: ${material.title}`
                            );
                            continue;
                          }

                          const materialId =
                            uploadingLearningMaterial.data.materialId;
                          material.id = materialId;

                          // Update localStorage after material creation
                          const updatedTopicsForStorage = topics.map((t) => {
                            const {
                              learningMaterials,
                              ...topicWithoutMaterials
                            } = t;
                            return {
                              ...topicWithoutMaterials,
                              learningMaterials:
                                learningMaterials?.map((m) => {
                                  const { content, ...materialWithoutContent } =
                                    m;
                                  return {
                                    ...materialWithoutContent,
                                    content: content
                                      ? {
                                          filename: content.filename,
                                          fileSize: content.fileSize,
                                          fileType: content.fileType,
                                          fileCategory: content.fileCategory,
                                        }
                                      : null,
                                  };
                                }) || [],
                            };
                          });
                          localStorage.setItem(
                            "topics",
                            JSON.stringify(updatedTopicsForStorage)
                          );

                          // Handle quiz creation for new materials
                          if (test?.[0]?.questionSets?.length > 0) {
                            try {
                              const quizTest = {
                                title: `${material.title} Quiz`,
                                type: TestType.QUIZ,
                                difficulty: TestDifficulty.INTERMEDIATE,
                                passingScore: test[0]?.passingScore ?? 0,
                                showCorrectAnswers: true,
                                maxAttempts: test[0]?.maxAttempts ?? 10000,
                                allowRetakes: true,
                                enableRandomization: true,
                                questionsPerTest: test[0]?.questionSets.length,
                                gradingType: GradingType.AUTOMATIC,
                                learningMaterialId: materialId,
                              };

                              const quizCreation =
                                await testApi.createTest(quizTest);
                              if (
                                !quizCreation?.success ||
                                !quizCreation?.data?.id
                              ) {
                                console.error(
                                  `Failed to create quiz for material: ${material.title}`
                                );
                                continue;
                              }

                              test[0].id = quizCreation.data.id;
                              const quizId = quizCreation.data.id;

                              for (const quizSet of test[0]?.questionSets ||
                                []) {
                                try {
                                  const quizSetCreation =
                                    await testApi.createQuestionSet({
                                      name: quizSet.title,
                                      testId: quizId,
                                      weight: quizSet.weight ?? 1,
                                    });

                                  if (
                                    !quizSetCreation?.success ||
                                    !quizSetCreation?.data?.id
                                  ) {
                                    console.error(
                                      `Failed to create quiz set: ${quizSet.title}`
                                    );
                                    continue;
                                  }

                                  quizSet.id = quizSetCreation.data.id;
                                  const quizSetId = quizSetCreation.data.id;

                                  for (const quizQuestion of quizSet.questions ||
                                    []) {
                                    try {
                                      const questionCreation =
                                        await testApi.createQuestion({
                                          body: [
                                            {
                                              type: TestQuestionType.SINGLE_SELECT,
                                              question: quizQuestion.question,
                                              options:
                                                quizQuestion.options || [],
                                              correctAnswer:
                                                quizQuestion.correctAnswer,
                                              explanation:
                                                quizQuestion.explanation,
                                              tags: quizQuestion.tags || [],
                                              difficulty:
                                                TestDifficulty.INTERMEDIATE,
                                              points: 1,
                                            },
                                          ],
                                          testId: quizId,
                                          questionSetId: quizSetId,
                                        });

                                      if (
                                        questionCreation?.success &&
                                        questionCreation?.data &&
                                        Array.isArray(questionCreation.data) &&
                                        questionCreation.data.length > 0
                                      ) {
                                        quizQuestion.id =
                                          questionCreation.data[0].id;
                                      } else {
                                        console.error(
                                          `Failed to create question: ${quizQuestion.question}`
                                        );
                                      }
                                    } catch (questionError) {
                                      console.error(
                                        "Error creating question:",
                                        questionError
                                      );
                                      continue;
                                    }
                                  }
                                } catch (quizSetError) {
                                  console.error(
                                    "Error creating quiz set:",
                                    quizSetError
                                  );
                                  continue;
                                }
                              }
                            } catch (quizError) {
                              console.error("Error creating quiz:", quizError);
                              continue;
                            }
                          }
                        } catch (materialError) {
                          console.error(
                            "Error processing material:",
                            materialError
                          );
                          continue;
                        }
                      }
                    } else {
                      // Updating existing topic
                      const storedTopic = topicData.find(
                        (t) => t.id === topic.id
                      );
                      if (storedTopic) {
                        const {
                          learningMaterials: savedLearningMaterials,
                          ...savedTopicDataWithoutCourseId
                        } = storedTopic;
                        const savedTopicData = {
                          ...savedTopicDataWithoutCourseId,
                          courseId,
                        };

                        const {
                          learningMaterials: formLearningMaterials,
                          ...updatedTopicDataWithoutCourseId
                        } = topic;
                        const updatedTopicData = {
                          ...updatedTopicDataWithoutCourseId,
                          courseId,
                        };

                        const isTopicSame = areObjectsEqual(
                          savedTopicData,
                          updatedTopicData
                        );

                        // Update topic only if changed
                        if (!isTopicSame) {
                          try {
                            await updateTopic(topic.id, updatedTopicData);
                          } catch (topicUpdateError) {
                            console.error(
                              "Error updating topic:",
                              topicUpdateError
                            );
                            continue;
                          }
                        }

                        for (const material of formLearningMaterials || []) {
                          try {
                            const { test, ...materialData } = material;
                            const updatedMaterialData = {
                              topicId: topic.id,
                              title: materialData.title,
                              description: materialData.description,
                              displayOrder: material.displayOrder,
                              tags: materialData.tags || [],
                              allowDownload: materialData.allowDownload ?? true,
                              drmEnabled: materialData.drmEnabled ?? true, // Fixed typo
                            };

                            // Check if material is new
                            if (material.id.startsWith("material-")) {
                              // Creating new material in existing topic
                              let contentType;
                              switch (materialData.content?.fileType) {
                                case "PPTX":
                                  contentType =
                                    "application/vnd.openxmlformats-officedocument.presentationml.presentation";
                                  break;
                                case "PDF":
                                  contentType = "application/pdf";
                                  break;
                                case "VIDEO":
                                  contentType = "video/mp4";
                                  break;
                                default:
                                  contentType = "application/octet-stream";
                              }

                              const materialDataFile = {
                                filename: material.content?.filename,
                                fileSize: material.content?.fileSize,
                                fileType:
                                  material.content?.fileType === "PPTX"
                                    ? "DOCUMENT"
                                    : material.content?.fileType,
                                contentType,
                                category: material.content?.fileCategory,
                                duration: material.duration,
                              };

                              const finalPayload = {
                                ...materialDataFile,
                                learningMaterial: updatedMaterialData,
                              };

                              const uploadingLearningMaterial =
                                await uploadLearningMaterial(
                                  material.content?.file,
                                  finalPayload
                                );

                              if (
                                !uploadingLearningMaterial?.success ||
                                !uploadingLearningMaterial?.data?.materialId
                              ) {
                                console.error(
                                  `Failed to upload new material: ${material.title}`
                                );
                                continue;
                              }

                              const materialId =
                                uploadingLearningMaterial.data.materialId;
                              material.id = materialId;

                              // Handle quiz for new material
                              if (test?.[0]?.questionSets?.length > 0) {
                                try {
                                  const quizTest = {
                                    title: `${material.title} Quiz`,
                                    type: TestType.QUIZ,
                                    difficulty: TestDifficulty.INTERMEDIATE,
                                    passingScore: test[0]?.passingScore ?? 0,
                                    showCorrectAnswers: true,
                                    maxAttempts: test[0]?.maxAttempts ?? 10000,
                                    allowRetakes: true,
                                    enableRandomization: true,
                                    questionsPerTest:
                                      test[0]?.questionSets.length,
                                    gradingType: GradingType.AUTOMATIC,
                                    learningMaterialId: materialId,
                                  };

                                  const quizCreation =
                                    await testApi.createTest(quizTest);
                                  if (
                                    !quizCreation?.success ||
                                    !quizCreation?.data?.id
                                  ) {
                                    console.error(
                                      `Failed to create quiz for new material: ${material.title}`
                                    );
                                    continue;
                                  }

                                  test[0].id = quizCreation.data.id;
                                  const quizId = quizCreation.data.id;

                                  for (const quizSet of test[0]?.questionSets ||
                                    []) {
                                    try {
                                      const quizSetCreation =
                                        await testApi.createQuestionSet({
                                          name: quizSet.title,
                                          testId: quizId,
                                          weight: quizSet.weight ?? 1,
                                        });

                                      if (
                                        !quizSetCreation?.success ||
                                        !quizSetCreation?.data?.id
                                      ) {
                                        console.error(
                                          `Failed to create quiz set: ${quizSet.title}`
                                        );
                                        continue;
                                      }

                                      quizSet.id = quizSetCreation.data.id;
                                      const quizSetId = quizSetCreation.data.id;

                                      for (const quizQuestion of quizSet.questions ||
                                        []) {
                                        try {
                                          const questionCreation =
                                            await testApi.createQuestion({
                                              body: [
                                                {
                                                  type: TestQuestionType.SINGLE_SELECT,
                                                  question:
                                                    quizQuestion.question,
                                                  options:
                                                    quizQuestion.options || [],
                                                  correctAnswer:
                                                    quizQuestion.correctAnswer,
                                                  explanation:
                                                    quizQuestion.explanation,
                                                  tags: quizQuestion.tags || [],
                                                  difficulty:
                                                    TestDifficulty.INTERMEDIATE,
                                                  points: 1,
                                                },
                                              ],
                                              testId: quizId,
                                              questionSetId: quizSetId,
                                            });

                                          if (
                                            questionCreation?.success &&
                                            questionCreation?.data &&
                                            Array.isArray(questionCreation.data) &&
                                            questionCreation.data.length > 0
                                          ) {
                                            quizQuestion.id =
                                              questionCreation.data[0].id;
                                          }
                                        } catch (questionError) {
                                          console.error(
                                            "Error creating question:",
                                            questionError
                                          );
                                          continue;
                                        }
                                      }
                                    } catch (quizSetError) {
                                      console.error(
                                        "Error creating quiz set:",
                                        quizSetError
                                      );
                                      continue;
                                    }
                                  }
                                } catch (quizError) {
                                  console.error(
                                    "Error creating quiz for new material:",
                                    quizError
                                  );
                                  continue;
                                }
                              }
                            } else {
                              // Updating existing material
                              const existingMaterial =
                                savedLearningMaterials?.find(
                                  (m) => m.id === material.id
                                );

                              if (existingMaterial) {
                                const {
                                  test: existingTest,
                                  ...existingMaterialData
                                } = existingMaterial;

                                const savedMaterialData = {
                                  title: existingMaterialData.title,
                                  description: existingMaterialData.description,
                                  displayOrder:
                                    existingMaterialData.displayOrder,
                                  tags: existingMaterialData.tags || [],
                                  allowDownload:
                                    existingMaterialData.allowDownload ?? true,
                                  drmEnabled:
                                    existingMaterialData.drmEnabled ?? true, // Fixed typo
                                };

                                const isMaterialSame = areObjectsEqual(
                                  savedMaterialData,
                                  updatedMaterialData
                                );

                                // Update material if changed or file replaced
                                if (
                                  !isMaterialSame ||
                                  material.content?.file instanceof File
                                ) {
                                  try {
                                    if (
                                      material.content?.file instanceof File
                                    ) {
                                      const fileUpload = await handleFileUpload(
                                        material.content.file,
                                        "LEARNING_MATERIAL"
                                      );
                                      updatedMaterialData.fileUploadId =
                                        fileUpload.fileUploadId;
                                    }

                                    const updateMaterialData =
                                      await updateMaterial(
                                        material.id,
                                        updatedMaterialData
                                      );

                                    if (!updateMaterialData?.success) {
                                      console.error(
                                        `Failed to update material: ${material.title}`
                                      );
                                      continue;
                                    }
                                  } catch (materialUpdateError) {
                                    console.error(
                                      "Error updating material:",
                                      materialUpdateError
                                    );
                                    continue;
                                  }
                                }

                                // Handle quiz updates for existing material
                                if (test?.[0]) {
                                  try {
                                    if (test[0].id.startsWith("test-")) {
                                      // Creating new quiz for existing material
                                      if (test[0]?.questionSets?.length > 0) {
                                        const quizTest = {
                                          title: `${material.title} Quiz`,
                                          type: TestType.QUIZ,
                                          difficulty:
                                            TestDifficulty.INTERMEDIATE,
                                          passingScore:
                                            test[0]?.passingScore ?? 0,
                                          showCorrectAnswers: true,
                                          maxAttempts:
                                            test[0]?.maxAttempts ?? 10000,
                                          allowRetakes: true,
                                          enableRandomization: true,
                                          questionsPerTest:
                                            test[0]?.questionSets.length,
                                          gradingType: GradingType.AUTOMATIC,
                                          learningMaterialId: material.id,
                                        };

                                        const quizCreation =
                                          await testApi.createTest(quizTest);
                                        if (
                                          !quizCreation?.success ||
                                          !quizCreation?.data?.id
                                        ) {
                                          console.error(
                                            `Failed to create quiz for existing material: ${material.title}`
                                          );
                                          continue;
                                        }

                                        test[0].id = quizCreation.data.id;
                                        const quizId = quizCreation.data.id;

                                        for (const quizSet of test[0]
                                          ?.questionSets || []) {
                                          try {
                                            const quizSetCreation =
                                              await testApi.createQuestionSet({
                                                name: quizSet.title,
                                                testId: quizId,
                                                weight: quizSet.weight ?? 1,
                                              });

                                            if (
                                              !quizSetCreation?.success ||
                                              !quizSetCreation?.data?.id
                                            ) {
                                              console.error(
                                                `Failed to create quiz set: ${quizSet.title}`
                                              );
                                              continue;
                                            }

                                            quizSet.id =
                                              quizSetCreation.data.id;
                                            const quizSetId =
                                              quizSetCreation.data.id;

                                            for (const quizQuestion of quizSet.questions ||
                                              []) {
                                              try {
                                                const questionCreation =
                                                  await testApi.createQuestion({
                                                    body: [
                                                      {
                                                        type: TestQuestionType.SINGLE_SELECT,
                                                        question:
                                                          quizQuestion.question,
                                                        options:
                                                          quizQuestion.options ||
                                                          [],
                                                        correctAnswer:
                                                          quizQuestion.correctAnswer,
                                                        explanation:
                                                          quizQuestion.explanation,
                                                        tags:
                                                          quizQuestion.tags ||
                                                          [],
                                                        difficulty:
                                                          TestDifficulty.INTERMEDIATE,
                                                        points: 1,
                                                      },
                                                    ],
                                                    testId: quizId,
                                                    questionSetId: quizSetId,
                                                  });

                                                if (
                                                  questionCreation?.success &&
                                                  questionCreation?.data &&
                                                  Array.isArray(questionCreation.data) &&
                                                  questionCreation.data.length > 0
                                                ) {
                                                  quizQuestion.id =
                                                    questionCreation.data[0].id;
                                                }
                                              } catch (questionError) {
                                                console.error(
                                                  "Error creating question:",
                                                  questionError
                                                );
                                                continue;
                                              }
                                            }
                                          } catch (quizSetError) {
                                            console.error(
                                              "Error creating quiz set:",
                                              quizSetError
                                            );
                                            continue;
                                          }
                                        }
                                      }
                                    } else {
                                      // Updating existing quiz
                                      if (test[0]?.questionSets?.length > 0) {
                                        const quizId = test[0].id; // Fixed: Added missing quizId
                                        const existingTest =
                                          existingMaterial?.test?.[0];

                                        for (const quizSet of test[0]
                                          .questionSets || []) {
                                          try {
                                            const existingQuizSet =
                                              existingTest?.questionSets?.find(
                                                (qs) => qs.id === quizSet.id
                                              );

                                            if (existingQuizSet) {
                                              // Update existing quiz set questions
                                              for (const quizQuestion of quizSet.questions ||
                                                []) {
                                                try {
                                                  const existingQuestion =
                                                    existingQuizSet?.questions?.find(
                                                      (q) =>
                                                        q.id === quizQuestion.id
                                                    );

                                                  const updatedQuizQuestion = {
                                                    type: TestQuestionType.SINGLE_SELECT,
                                                    question:
                                                      quizQuestion.question,
                                                    options:
                                                      quizQuestion.options ||
                                                      [],
                                                    correctAnswer:
                                                      quizQuestion.correctAnswer,
                                                    explanation:
                                                      quizQuestion.explanation,
                                                    tags:
                                                      quizQuestion.tags || [],
                                                    difficulty:
                                                      TestDifficulty.INTERMEDIATE,
                                                    points: 1,
                                                  };

                                                  if (existingQuestion) {
                                                    if (
                                                      !areObjectsEqual(
                                                        existingQuestion,
                                                        quizQuestion
                                                      )
                                                    ) {
                                                      const questionUpdate =
                                                        await testApi.updateQuestion(
                                                          quizQuestion.id,
                                                          updatedQuizQuestion
                                                        );
                                                      if (
                                                        !questionUpdate?.success
                                                      ) {
                                                        console.error(
                                                          `Failed to update question: ${quizQuestion.question}`
                                                        );
                                                      }
                                                    }
                                                  } else {
                                                    // Create new question in existing quiz set
                                                    const questionCreation =
                                                      await testApi.createQuestion(
                                                        {
                                                          body: [
                                                            updatedQuizQuestion,
                                                          ],
                                                          testId: quizId,
                                                          questionSetId:
                                                            quizSet.id,
                                                        }
                                                      );

                                                    if (
                                                      questionCreation?.success &&
                                                      Array.isArray(questionCreation?.data) &&
                                                      questionCreation.data.length > 0
                                                    ) {
                                                      quizQuestion.id =
                                                        questionCreation.data[0].id;
                                                    }
                                                  }
                                                } catch (questionError) {
                                                  console.error(
                                                    "Error processing question:",
                                                    questionError
                                                  );
                                                  continue;
                                                }
                                              }
                                            } else {
                                              // Create new quiz set in existing quiz
                                              const quizSetCreation =
                                                await testApi.createQuestionSet(
                                                  {
                                                    name: quizSet.title,
                                                    testId: quizId,
                                                    weight: quizSet.weight ?? 1,
                                                  }
                                                );

                                              if (
                                                !quizSetCreation?.success ||
                                                !quizSetCreation?.data?.id
                                              ) {
                                                console.error(
                                                  `Failed to create new quiz set: ${quizSet.title}`
                                                );
                                                continue;
                                              }

                                              quizSet.id =
                                                quizSetCreation.data.id;
                                              const quizSetId =
                                                quizSetCreation.data.id;

                                              for (const quizQuestion of quizSet.questions ||
                                                []) {
                                                try {
                                                  const questionCreation =
                                                    await testApi.createQuestion(
                                                      {
                                                        body: [
                                                          {
                                                            type: TestQuestionType.SINGLE_SELECT,
                                                            question:
                                                              quizQuestion.question,
                                                            options:
                                                              quizQuestion.options ||
                                                              [],
                                                            correctAnswer:
                                                              quizQuestion.correctAnswer,
                                                            explanation:
                                                              quizQuestion.explanation,
                                                            tags:
                                                              quizQuestion.tags ||
                                                              [],
                                                            difficulty:
                                                              TestDifficulty.INTERMEDIATE,
                                                            points: 1,
                                                          },
                                                        ],
                                                        testId: quizId,
                                                        questionSetId:
                                                          quizSetId,
                                                      }
                                                    );

                                                  if (
                                                    questionCreation?.success &&
                                                    questionCreation?.data &&
                                                    Array.isArray(questionCreation.data) &&
                                                    questionCreation.data.length > 0
                                                  ) {
                                                    quizQuestion.id =
                                                      questionCreation.data[0].id;
                                                  }
                                                } catch (questionError) {
                                                  console.error(
                                                    "Error creating question in new quiz set:",
                                                    questionError
                                                  );
                                                  continue;
                                                }
                                              }
                                            }
                                          } catch (quizSetError) {
                                            console.error(
                                              "Error processing quiz set:",
                                              quizSetError
                                            );
                                            continue;
                                          }
                                        }
                                      }
                                    }
                                  } catch (testError) {
                                    console.error(
                                      "Error processing test:",
                                      testError
                                    );
                                    continue;
                                  }
                                }
                              }
                            }
                          } catch (materialError) {
                            console.error(
                              "Error processing existing material:",
                              materialError
                            );
                            continue;
                          }
                        }
                      }
                    }
                  } catch (topicError) {
                    console.error("Error processing topic:", topicError);
                    continue;
                  }
                }

                // Final localStorage sync - save current state without File objects
                const finalTopicsForStorage = topics.map((t) => {
                  const { learningMaterials, ...topicWithoutMaterials } = t;
                  return {
                    ...topicWithoutMaterials,
                    learningMaterials:
                      learningMaterials?.map((m) => {
                        const { content, ...materialWithoutContent } = m;
                        return {
                          ...materialWithoutContent,
                          content: content
                            ? {
                                filename: content.filename,
                                fileSize: content.fileSize,
                                fileType: content.fileType,
                                fileCategory: content.fileCategory,
                              }
                            : null,
                        };
                      }) || [],
                  };
                });
                localStorage.setItem(
                  "topics",
                  JSON.stringify(finalTopicsForStorage)
                );

                setCurrentStep((prev) => prev + 1);
                setLoading(false);

                const isValid = await validateAndShowErrors();
                if (isValid) {
                  console.log("Topics form is valid, proceeding to next step");
                } else {
                  console.log("Topics form has errors:", validationErrors);
                }
              } catch (error) {
                console.error("Critical error in topic processing:", error);
                setLoading(false);

                // Save whatever progress was made
                const emergencyTopicsForStorage = topics.map((t) => {
                  const { learningMaterials, ...topicWithoutMaterials } = t;
                  return {
                    ...topicWithoutMaterials,
                    learningMaterials:
                      learningMaterials?.map((m) => {
                        const { content, ...materialWithoutContent } = m;
                        return {
                          ...materialWithoutContent,
                          content: content
                            ? {
                                filename: content.filename,
                                fileSize: content.fileSize,
                                fileType: content.fileType,
                                fileCategory: content.fileCategory,
                              }
                            : null,
                        };
                      }) || [],
                  };
                });
                localStorage.setItem(
                  "topics",
                  JSON.stringify(emergencyTopicsForStorage)
                );
              }
            }}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving
              </>
            ) : (
              "Save & Next"
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default TopicsStep;
